/*
Author: Chathuraka Mallawa Arachchi
Date: 25/09/2024
Copyright © 2024 Infix!. All rights reserved.
*/

import React, { useCallback, useEffect, useState } from 'react'
import debounce from 'lodash/debounce'
import Errors from './Error'

const InputField = (props: any) => {
    const {
        name,
        onChange,
        value,
        propClasses,
        propDivClasses = '',
        isInValid = true,
        form,
        onBlur,
        disabled,
        readOnly = false,
        showErrors = true,
        type,
        decimalPlaces,
        validate
    } = props
    //const [textValue, setTextValue] = React.useState<string> ();
    const [isFocused, setIsFocused] = useState(false)
    const [rawValue, setRawValue] = useState('')
    const [isActive, setIsActive] = useState(false)

    const debouncedOnChange = useCallback(
        debounce((cleaned: string, name: string) => {
            onChange(cleaned, name)
        }, 500),
        [onChange]
    )

    const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target
        let cleaned = value
        if (validate && !validate(value)) return;
        if (type === 'Number') {
            cleaned = cleaned.replace(/,/g, '') // remove commas for parsing
            // If decimalPlaces is specified, enforce it during typing
            if (decimalPlaces !== undefined && decimalPlaces >= 0) {
                const parts = cleaned.split('.')
                if (parts.length === 2 && parts[1].length > decimalPlaces) {
                    // Don't allow typing more decimal places than specified
                    return
                }
            }

            // If decimalPlaces is 0, don't allow decimal points
            if (decimalPlaces === 0 && cleaned.includes('.')) {
                return
            }

            if (decimalPlaces !== undefined && cleaned.charAt(cleaned.length - 1) === '.') {
                setRawValue(value) // allow user to type decimal point
                debouncedOnChange(cleaned, name)
                return
            }

            if (rawValue.endsWith('.') && !cleaned.endsWith('.') && value.length < rawValue.length) {
                setRawValue(value) // allow user to delete decimal point
                debouncedOnChange(cleaned, name)
                return
            }

            setRawValue(formatWithCommas(cleaned))
        } else {
            setRawValue(value)
        }
        debouncedOnChange(cleaned, name)
    }
    const handleOnBlure = () => {
        setIsFocused(false)
        onBlur && onBlur()
    }
    useEffect(() => {
        if (type === 'Number') {
            const formatted = formatWithCommas(value)
            setRawValue(formatted)
            // setRawValue(formatNumber(value))
        } else {
            setRawValue(value)
        }
    }, [value])

    const formatWithCommas = (value: string) => {
        if (!value) return ''

        // Allow only digits and one decimal point
        const cleaned = value.replace(/[^\d.]/g, '')
        const parts = cleaned.split('.')

        // Format the integer part with commas
        const integerPart = parts[0] ? Number(parts[0]).toLocaleString() : ''

        // Re-append decimal if it's present or being typed
        if (parts.length === 2) {
            return `${integerPart}.${parts[1]}`
        } else if (value.endsWith('.')) {
            return `${integerPart}.` // preserves trailing dot
        }

        return integerPart
    }
    return (
        <div className={`${isInValid ? 'error' : ''}`}>
            <div
                className={`${propDivClasses} input-div ${!readOnly && isFocused ? 'input-div-focus' : ''} ${!readOnly && isActive ? 'input-div-active' : ''} ${disabled ? 'disabled' : ''}`}
            >
                <input
                    {...props}
                    type={type === 'Number' ? 'Text' : type}
                    readOnly={readOnly}
                    className={propClasses}
                    onChange={handleDateChange}
                    value={rawValue}
                    onFocus={() => setIsFocused(true)}
                    onBlur={handleOnBlure}
                    onMouseDown={() => setIsActive(true)}
                    onMouseUp={() => setIsActive(false)}
                />
            </div>
            {form && showErrors ? (
                <Errors
                    fieldName={name}
                    validation={form[name] ? form[name] : undefined}
                />
            ) : (
                ''
            )}
        </div>
    )
}
export default InputField
